import React from "react";
import cookie from "react-cookies";
import { useTranslation, Trans } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { Link as ReactLink, generatePath } from "react-router-dom";

import { InfoOutline } from "@airhelp/icons";
import {
  Circle,
  HStack,
  Heading,
  VStack,
  Text,
  Link,
  Tooltip,
  Image,
  Box,
  useDisclosure,
  useBreakpointValue,
  Button,
  Flex,
  Stack,
} from "@chakra-ui/react";

import earthImage from "assets/images/earth@2x.png";
import tipsSpotImage from "assets/images/spot-illustrations/tips.png";
import config from "config/config";
import { ITEM_LIST_NAME_COOKIE } from "config/cookies";
import { PAGE_PATHS } from "config/routes";
import { useLocale } from "contexts/LocaleContext/LocaleContextProvider";
import { CopyToClipboardButton } from "elements";
import { UserProfile } from "models/UserProfile";
import { useTrackEvent, useTrackOnce } from "utils/tracking/hooks";

import { getAiraloLinkByLocale } from "./airaloLinkHelper";

interface IComponent {
  eventPlacement: string;
  userProfile: UserProfile;
}

const ESimBanner: React.FC<IComponent> = ({ eventPlacement, userProfile }) => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { trackCtaClick, trackPageInteractions } = useTrackEvent();

  const { isOpen, onOpen, onToggle, onClose } = useDisclosure();

  const airaloLink = getAiraloLinkByLocale(locale);

  const visibilityTreshhold = useBreakpointValue({ base: 0.75, lg: 0.5 });

  const { ref, inView } = useInView({
    threshold: visibilityTreshhold,
  });

  const showMembershipUpsell = userProfile.policy.perks.esim?.type === "trip";

  useTrackOnce(
    (setTracked) => {
      if (inView) {
        if (showMembershipUpsell) {
          trackPageInteractions("show", "upgrade to b2c url from esim");
        } else {
          trackPageInteractions("show", "esim banner");
        }
        setTracked();
      }
    },
    [inView, showMembershipUpsell]
  );

  const handleUpgradeClick = () => {
    trackCtaClick("upgrade to b2c url from esim", eventPlacement);

    cookie.save(ITEM_LIST_NAME_COOKIE, "esim_dashboard_upgrade", config.cookieOptions);
  };

  const handleTooltipIconClick = () => {
    trackCtaClick("esim info", eventPlacement);

    onToggle();
  };

  return (
    <Box w={{ base: "100%", lg: "50%" }} flex="1 1 50%" id="esim-banner" data-testid="esim-banner" ref={ref}>
      <Text
        textTransform="uppercase"
        color="greyscale.600"
        fontSize="xs"
        justifySelf="flex-start"
        fontWeight="bold"
        mb={3}
        mt={-7}
      >
        {t("esim.banner.travelling_abroad")}
      </Text>
      <VStack borderRadius="xl" overflow="hidden" height={{ base: "unset", md: "100%" }} zIndex="0" spacing={0}>
        {/* esim code */}
        <VStack
          spacing={5}
          p={{ base: 4, md: 8 }}
          bgColor="primary.900"
          position="relative"
          alignItems="flex-start"
          w="100%"
          flexGrow="1"
        >
          <Image
            src={earthImage}
            alt=""
            position="absolute"
            objectPosition="center"
            maxHeight="100%"
            maxWidth="60%"
            bottom="0"
            right="0"
          />
          <HStack justifyContent="space-between" spacing={4} width="100%">
            <VStack alignItems="flex-start" spacing={0}>
              <Heading variant="h5" color="primary.100">
                <Trans i18nKey="esim.banner.header" values={{ discount: userProfile.policy.perks.esim?.discount }} />
              </Heading>
              <Text fontSize="md" color="primary.200">
                {t("esim.banner.description")}
              </Text>
            </VStack>
            <Tooltip
              hasArrow
              label={t("esim.banner.tooltip")}
              placement="top-start"
              bgColor="greyscale.800"
              borderRadius="md"
              p={4}
              isOpen={isOpen}
              onOpen={() => trackPageInteractions("show", "esim info")}
            >
              <InfoOutline
                color="greyscale.100"
                mt={1}
                boxSize={5}
                cursor="pointer"
                display="inline"
                data-testid="tooltip-info-icon"
                alignSelf="flex-start"
                onMouseEnter={onOpen}
                onMouseLeave={onClose}
                onClick={handleTooltipIconClick}
                zIndex="1"
              />
            </Tooltip>
          </HStack>
          <VStack align="start">
            <HStack spacing={2}>
              <Circle size={6} bg="primary.700">
                <Text color="primary.100" fontSize="sm" fontWeight="medium">
                  1
                </Text>
              </Circle>
              <Text fontSize="md" color="primary.200" zIndex="1">
                {t("esim.banner.get_promocode")}
              </Text>
            </HStack>
            <CopyToClipboardButton
              id="esim"
              inputValue={userProfile.policy.perks.esim ? userProfile.policy.perks.esim.code : ""}
              eventPlacement={eventPlacement}
              eventLabel={`esim code copy ${userProfile.policy.membership?.tier}`}
            />
          </VStack>
          <HStack spacing={2} zIndex="1">
            <Circle size={6} bg="primary.700">
              <Text color="primary.100" fontSize="sm" fontWeight="medium">
                2
              </Text>
            </Circle>
            <Text fontSize="md" color="primary.200">
              <Trans
                i18nKey="esim.banner.use_it_on"
                components={{
                  a: (
                    <Link
                      as={ReactLink}
                      to={airaloLink}
                      onClick={() => trackCtaClick("airalo url", "dashboard")}
                      color="danger.500"
                      fontSize="inherit"
                      fontWeight="normal"
                      _visited={{}}
                    />
                  ),
                }}
              />
            </Text>
          </HStack>
        </VStack>
        {/* upgrade your account */}
        {showMembershipUpsell ? (
          <Stack
            spacing={{ base: 4, md: 8 }}
            px={{ base: 1, md: 4 }}
            py={{ base: 4, md: 4 }}
            direction={{ base: "column", md: "row" }}
            alignItems="center"
            bgColor="primary.700"
            w="100%"
          >
            <HStack spacing={2} alignItems="center" w="100%">
              <Box flexShrink="0">
                <Image src={tipsSpotImage} h="96px" w="auto" />
              </Box>

              <VStack spacing={2} alignItems="flex-start">
                <Text
                  color="primary.200"
                  textTransform="uppercase"
                  fontSize="xs"
                  fontWeight="medium"
                  letterSpacing={1.5}
                >
                  {t("esim.banner.upgrade_your_account")}
                </Text>
                <Text color="primary.200" fontSize="lg">
                  {t("esim.banner.even_more_discounts")}
                </Text>
              </VStack>
            </HStack>

            <Flex flexShrink="0" mx={{ base: 0, md: 4 }} w={{ base: "100%", md: "auto" }}>
              <Button
                as={ReactLink}
                to={{
                  pathname: generatePath(PAGE_PATHS.MEMBERSHIPS.BUY_PAGE),
                  search: "channel=esim_upgrade_essential",
                }}
                variant="secondary"
                borderColor="transparent"
                size="lg"
                mx={{ base: 4, md: 0 }}
                w={{ base: "100%", md: "auto" }}
                textTransform="capitalize"
                onClick={handleUpgradeClick}
                data-testid="learn-more-button"
              >
                {t("common.learn_more")}
              </Button>
            </Flex>
          </Stack>
        ) : null}
      </VStack>
    </Box>
  );
};

export default ESimBanner;
